import { useEffect, useRef, useState } from "react";
import { api, formatApiError, API_BASE } from "../lib/api";
import { Upload, FileSpreadsheet, CheckCircle2, History } from "lucide-react";
import { toast } from "sonner";

function Dropzone({ testId, title, hint, endpoint, onDone, accept = ".xlsx,.xlsm" }) {
  const [drag, setDrag] = useState(false);
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);

  const upload = async (file) => {
    if (!file) return;
    if (!file.name.match(/\.xlsx?$/i)) return toast.error("Please upload an .xlsx file");
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post(endpoint, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onDone?.(data);
      const msg = data?.message
        || (data?.filename
              ? `Previous data cleared. Dashboard is now showing data from ${data.filename}`
              : "Upload successful");
      toast.success(msg, { duration: 6000 });
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setUploading(false); }
  };

  return (
    <div
      data-testid={testId}
      onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
      onDragLeave={() => setDrag(false)}
      onDrop={(e) => { e.preventDefault(); setDrag(false); upload(e.dataTransfer.files?.[0]); }}
      onClick={() => inputRef.current?.click()}
      className={`bg-[#12141D] border-2 border-dashed p-10 text-center cursor-pointer transition-colors
        ${drag ? "border-[#00D4FF] bg-[#00D4FF]/5" : "border-border hover:border-[#00D4FF]/60"}`}
    >
      <input ref={inputRef} type="file" accept={accept} className="hidden"
        onChange={(e) => upload(e.target.files?.[0])} />
      <Upload className="mx-auto text-[#00D4FF] mb-3" size={36} />
      <div className="font-heading font-bold text-lg">
        {uploading ? "Processing…" : title}
      </div>
      <div className="text-xs text-muted-foreground mt-2 tracking-widest uppercase">{hint}</div>
    </div>
  );
}

export default function UploadData() {
  const [weekly, setWeekly] = useState(null);
  const [historical, setHistorical] = useState(null);
  const [budgetStatus, setBudgetStatus] = useState(null);
  const [history, setHistory] = useState([]);

  const refresh = async () => {
    try {
      const [w, h, b, hist] = await Promise.all([
        api.get("/import/status"),
        api.get("/upload/historical/status"),
        api.get("/upload/jobs-budget/status"),
        api.get("/upload/history"),
      ]);
      setWeekly(w.data); setHistorical(h.data); setBudgetStatus(b.data); setHistory(hist.data || []);
    } catch { /* noop */ }
  };
  useEffect(() => { refresh(); }, []);

  return (
    <div className="space-y-6" data-testid="upload-page">
      <header>
        <div className="text-[11px] tracking-[0.35em] uppercase text-[#00D4FF]">DATA · Upload</div>
        <h1 className="font-heading font-bold text-4xl tracking-tight mt-2">Data Imports</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Drop the UD Weekly KPI Report and the Historical Jobs file. The dashboard rebuilds from these every time.
        </p>
      </header>

      <section>
        <div className="text-[10px] tracking-[0.3em] uppercase text-muted-foreground mb-3">1 · Weekly KPI Report</div>
        <Dropzone
          testId="upload-weekly"
          title="Drop UD_Weekly_KPI_Report.xlsx here or click to browse"
          hint="Replaces all dashboard data — production, hours, materials, NCR, finish"
          endpoint="/upload/excel"
          onDone={refresh}
        />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
          <div className="bg-[#12141D] border border-border p-5">
            <div className="flex items-center gap-2 mb-3">
              <FileSpreadsheet className="text-[#00D4FF]" size={18} />
              <h3 className="font-heading font-semibold">Last Weekly Import</h3>
            </div>
            {weekly?.imported_at ? (
              <>
                <div className="flex items-center gap-2 text-emerald-300 text-sm"><CheckCircle2 size={14}/> Successful</div>
                <div className="text-xs text-muted-foreground mt-1 font-mono">{new Date(weekly.imported_at).toLocaleString()}</div>
              </>
            ) : <div className="text-xs text-muted-foreground">No weekly imports yet</div>}
          </div>
          <div className="bg-[#12141D] border border-border p-5">
            <h3 className="font-heading font-semibold mb-3">Records in Database</h3>
            <div className="grid grid-cols-2 gap-3">
              {["production", "hours", "materials", "ncr"].map((k) => (
                <div key={k} className="bg-[#0A0B10] border border-border p-3">
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{k}</div>
                  <div className="font-mono text-2xl text-[#00D4FF]">{weekly?.counts?.[k]?.toLocaleString() || 0}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section data-testid="upload-history-section">
        <div className="text-[10px] tracking-[0.3em] uppercase text-muted-foreground mb-3">
          Upload History · Last 5
        </div>
        <div className="bg-[#12141D] border border-border overflow-x-auto">
          <table className="w-full text-sm" data-testid="upload-history-table">
            <thead className="bg-[#1A1C28] text-[10px] uppercase tracking-widest text-[#94A3B8]">
              <tr>
                <th className="py-3 px-4 text-left">File</th>
                <th className="py-3 px-4 text-left">Uploaded At</th>
                <th className="py-3 px-4 text-right">Rows</th>
                <th className="py-3 px-4 text-left">Uploaded By</th>
                <th className="py-3 px-4 text-left">Status</th>
              </tr>
            </thead>
            <tbody>
              {history.length === 0 && (
                <tr>
                  <td colSpan={5} className="py-8 text-center text-xs uppercase tracking-widest text-muted-foreground">
                    No upload history yet
                  </td>
                </tr>
              )}
              {history.map((h, i) => (
                <tr key={h.id || i} className="border-t border-border/40"
                    data-testid={`upload-history-row-${i}`}>
                  <td className="py-3 px-4 font-mono truncate max-w-[280px]" title={h.filename}>
                    {h.filename || "—"}
                  </td>
                  <td className="py-3 px-4 font-mono text-muted-foreground">
                    {h.uploaded_at ? new Date(h.uploaded_at).toLocaleString() : "—"}
                  </td>
                  <td className="py-3 px-4 font-mono text-right">
                    {h.rows ? Number(h.rows).toLocaleString() : "—"}
                  </td>
                  <td className="py-3 px-4 text-muted-foreground">{h.uploaded_by || "—"}</td>
                  <td className="py-3 px-4">
                    {h.status === "active" ? (
                      <span className="inline-flex items-center px-2 py-0.5 text-[10px] tracking-widest uppercase border border-emerald-500/40 bg-emerald-950/40 text-emerald-300 font-mono">
                        Active
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2 py-0.5 text-[10px] tracking-widest uppercase border border-border bg-[#0A0B10] text-muted-foreground font-mono">
                        Replaced
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section>
        <div className="text-[10px] tracking-[0.3em] uppercase text-muted-foreground mb-3">2 · Historical Jobs (for Job Forecasting Tool)</div>
        <Dropzone
          testId="upload-historical"
          title="Drop Historical_Jobs.xlsx here or click to browse"
          hint="Columns: Job Name · Phase · Category · SQFT · Budgeted Cost · Actual Hours · Status"
          endpoint="/upload/historical"
          onDone={refresh}
        />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
          <div className="bg-[#12141D] border border-border p-5">
            <div className="flex items-center gap-2 mb-3">
              <History className="text-[#00D4FF]" size={18} />
              <h3 className="font-heading font-semibold">Last Historical Import</h3>
            </div>
            {historical?.imported_at ? (
              <>
                <div className="flex items-center gap-2 text-emerald-300 text-sm"><CheckCircle2 size={14}/> Successful</div>
                <div className="text-xs text-muted-foreground mt-1 font-mono">
                  {historical.filename} · {new Date(historical.imported_at).toLocaleString()}
                </div>
              </>
            ) : <div className="text-xs text-muted-foreground">No historical imports yet — Job Forecasting Tool falls back to weekly data</div>}
          </div>
          <div className="bg-[#12141D] border border-border p-5">
            <h3 className="font-heading font-semibold mb-3">Historical Records</h3>
            <div className="bg-[#0A0B10] border border-border p-3 max-w-xs">
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground">historical_jobs</div>
              <div className="font-mono text-2xl text-[#00D4FF]">{historical?.count?.toLocaleString() || 0}</div>
            </div>
          </div>
        </div>
      </section>

      <section data-testid="upload-jobs-budget-section">
        <div className="text-[10px] tracking-[0.3em] uppercase text-muted-foreground mb-3">3 · Jobs Budget (Mold Job Performance card)</div>
        <Dropzone
          testId="upload-jobs-budget"
          title="Drop Jobs_Budget.xlsx here or click to browse"
          hint="Columns: Job · Phase · Category Name · Plant · Estimated_SQFT · Budget_Hours · Material Cost Budgeted"
          endpoint="/upload/jobs-budget"
          onDone={refresh}
        />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
          <div className="bg-[#12141D] border border-border p-5">
            <div className="flex items-center gap-2 mb-3">
              <FileSpreadsheet className="text-[#EC4899]" size={18} />
              <h3 className="font-heading font-semibold">Last Jobs Budget Import</h3>
            </div>
            {budgetStatus?.last?.uploaded_at ? (
              <>
                <div className="flex items-center gap-2 text-emerald-300 text-sm"><CheckCircle2 size={14}/> Successful</div>
                <div className="text-xs text-muted-foreground mt-1 font-mono">
                  {budgetStatus.last.filename} · {new Date(budgetStatus.last.uploaded_at).toLocaleString()}
                </div>
              </>
            ) : <div className="text-xs text-muted-foreground">No Jobs Budget uploaded yet — the Mold Job Performance card will be empty</div>}
          </div>
          <div className="bg-[#12141D] border border-border p-5">
            <h3 className="font-heading font-semibold mb-3">Budget Rows</h3>
            <div className="bg-[#0A0B10] border border-border p-3 max-w-xs">
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground">jobs_budget</div>
              <div className="font-mono text-2xl text-[#EC4899]">{budgetStatus?.count?.toLocaleString() || 0}</div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}